import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, Tooltip, alpha, useTheme, useMediaQuery, IconButton } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TerminalIcon from '@mui/icons-material/Terminal';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { debugService, isCapturePrivateElement } from '../../services/debugService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import DebugPanel from './DebugPanel';
import DebugSessionOverlay from './DebugSessionOverlay';
import { debugSessionController, useDebugSession } from '../../services/debugSessionController';

function useSessionTimer(enabled: boolean) {
  const [elapsed, setElapsed] = useState('0:00');
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      const ms = Date.now() - debugService.sessionStartTime;
      const totalSec = Math.floor(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      setElapsed(`${m}:${s.toString().padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [enabled]);
  return elapsed;
}

const DevModeIndicator: React.FC = () => {
  const theme = useTheme();
  const toast = useToast();
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width:767.95px)');
  const [state, setState] = useState({ enabled: debugService.isEnabled, count: 0, errors: 0, mobileCount: 0 });

  const isAdmin = useMemo(() => {
    const rn = user?.roleName?.toLowerCase() || '';
    return rn === 'super admin' || rn === 'admin';
  }, [user?.roleName]);

  useEffect(() => {
    const sync = () => setState({
      enabled: debugService.isEnabled,
      count: debugService.getEvents().length,
      errors: debugService.getErrorCount(),
      mobileCount: debugService.getMobileEventCount(),
    });
    sync();
    return debugService.subscribe(sync);
  }, []);
  const [panelOpen, setPanelOpen] = useState(false);
  const elapsed = useSessionTimer(state.enabled);

  // Debug Session Builder: restore an in-progress session after a hard refresh,
  // and let an admin start a new free-roam session from the banner.
  const debugSession = useDebugSession();
  useEffect(() => { void debugSessionController.resumeIfAny(); }, []);
  const handleStartSession = useCallback(async () => {
    // A closed-but-unfinished session resumes rather than being stranded —
    // the HUD's close button promises exactly this.
    const resumable = debugSessionController.resumablePointer();
    if (resumable && window.confirm(`Resume the open debug session "${resumable.title || 'untitled'}"? (Cancel starts a new one)`)) {
      await debugSessionController.resumeIfAny();
      if (debugSessionController.getState().active) return;
      toast.showToast('That session is no longer resumable — starting fresh', 'info');
    }
    const title = window.prompt('Name this debug session (a troubleshooting topic):', '');
    if (title === null) return; // cancelled
    void debugSessionController.start(title.trim() || 'Debug session', {
      role: user?.roleName || null,
      user: user?.email || (user as any)?.name || null,
      company: (user as any)?.companyName ?? null,
    });
  }, [user, toast]);

  const handleToggle = useCallback(() => {
    const nowEnabled = debugService.toggle();
    toast.showToast(
      nowEnabled
        ? 'Dev Mode ON — all events are being captured. Click the DEV badge to download.'
        : 'Dev Mode disabled',
      nowEnabled ? 'info' : 'warning',
    );
    // c3 — fetch /api/version so the deploy SHA is available in the export
    // header. Fire-and-forget; failure is silent.
    if (nowEnabled) {
      void debugService.fetchDeployVersion();
    }
  }, [toast]);

  // c3 — also fetch deploy version on mount if dev mode is already on (e.g.
  // page reload with localStorage flag set). Otherwise the first export
  // after a reload reports null deploy_sha until the user toggles off+on.
  useEffect(() => {
    if (debugService.isEnabled) {
      void debugService.fetchDeployVersion();
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        handleToggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleToggle, isAdmin]);

  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleTripleTap = useCallback(() => {
    tapCountRef.current += 1;
    if (tapCountRef.current === 1) {
      tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, 2000);
    }
    if (tapCountRef.current >= 3) {
      tapCountRef.current = 0;
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
      handleToggle();
    }
  }, [handleToggle]);

  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      debugService.logUnhandledError(e.message, e.filename, e.lineno, e.colno, e.error);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      debugService.logUnhandledRejection(e.reason);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // c4 v4 #2 — user interaction capture. Window-level listeners that fire
  // only when dev mode is enabled (the debugService.logUserInteraction call
  // is itself gated, so a disabled-but-listening path costs only the event-
  // listener dispatch). Captures clicks always; keydown only for "interesting"
  // keys (Enter / Escape / Delete / Backspace / Tab / arrows / modifier
  // combos). Pure typing into form fields would be too noisy.
  useEffect(() => {
    // Interactions with the debug tool's OWN UI (the session HUD, the picker,
    // the smoke HUD) are not app behavior — recording them buried the "steps
    // before capture" trail under the user's own note-typing (22 of 28 steps in
    // one prod finding). Skip them at the source.
    const isDebugToolTarget = (target: EventTarget | null): boolean =>
      target instanceof HTMLElement
      && !!target.closest('[data-debug-overlay], [data-debug-inspector], [data-smoke-hud]');

    const describeTarget = (target: EventTarget | null): Record<string, any> => {
      if (!(target instanceof HTMLElement)) return { type: 'non-element' };
      // Walk up to find the nearest .react-flow__node / .react-flow__edge if any.
      const nearestRfNode = target.closest('.react-flow__node');
      const nearestRfEdge = target.closest('.react-flow__edge');
      const nearestRfHandle = target.closest('.react-flow__handle');
      const out: Record<string, any> = {
        tag: target.tagName,
        id: target.id || undefined,
        class: (target.className && typeof target.className === 'string') ? target.className.slice(0, 200) : undefined,
        data_id: target.getAttribute('data-id') || undefined,
        data_testid: target.getAttribute('data-testid') || undefined,
        // A click inside a private region keeps no text (SEC-W4-054).
        text: isCapturePrivateElement(target) ? undefined : ((target.innerText || target.textContent || '').trim().slice(0, 80) || undefined),
      };
      if (nearestRfNode) out.in_node = nearestRfNode.getAttribute('data-id') || '?';
      if (nearestRfEdge) out.in_edge = nearestRfEdge.getAttribute('data-id') || nearestRfEdge.getAttribute('data-testid') || '?';
      if (nearestRfHandle) out.in_handle = nearestRfHandle.getAttribute('data-handleid') || '?';
      return out;
    };

    const onClick = (e: MouseEvent) => {
      if (!debugService.isEnabled || isDebugToolTarget(e.target)) return;
      debugService.logUserInteraction({
        kind: 'click',
        button: e.button,
        target: describeTarget(e.target),
        client_x: e.clientX,
        client_y: e.clientY,
        ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey,
      });
    };

    const INTERESTING_KEYS = new Set(['Enter', 'Escape', 'Delete', 'Backspace', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
    const onKeydown = (e: KeyboardEvent) => {
      if (!debugService.isEnabled || isDebugToolTarget(e.target)) return;
      const isInteresting = INTERESTING_KEYS.has(e.key) || e.ctrlKey || e.metaKey;
      if (!isInteresting) return;
      debugService.logUserInteraction({
        kind: 'keydown',
        key: e.key,
        code: e.code,
        target: describeTarget(e.target),
        ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey,
      });
    };

    const onMousedown = (e: MouseEvent) => {
      if (!debugService.isEnabled) return;
      // Only capture mousedown on react-flow surfaces — these typically start
      // a drag and the action that follows is interesting. Skipping general
      // mousedowns to keep the log clean.
      if (!(e.target instanceof HTMLElement)) return;
      if (!e.target.closest('.react-flow')) return;
      debugService.logUserInteraction({
        kind: 'mousedown',
        button: e.button,
        target: describeTarget(e.target),
        client_x: e.clientX,
        client_y: e.clientY,
      });
    };

    window.addEventListener('click', onClick, true);   // capture phase
    window.addEventListener('keydown', onKeydown, true);
    window.addEventListener('mousedown', onMousedown, true);
    return () => {
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKeydown, true);
      window.removeEventListener('mousedown', onMousedown, true);
    };
  }, []);

  if (!isAdmin) return null;

  const hasErrors = state.errors > 0;
  const accentColor = hasErrors ? '#ef4444' : theme.palette.primary.main;
  const isDark = theme.palette.mode === 'dark';

  return (
    <>
      {isMobile && !state.enabled && (
        <Box
          onClick={handleTripleTap}
          sx={{ position: 'fixed', top: 0, left: 0, width: 60, height: 60, zIndex: 10000 }}
        />
      )}

      {state.enabled && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0,
            bgcolor: hasErrors
              ? alpha('#ef4444', isDark ? 0.2 : 0.08)
              : alpha(theme.palette.primary.main, isDark ? 0.15 : 0.06),
            borderBottom: '1px solid',
            borderColor: alpha(accentColor, 0.35),
            px: 1,
          }}
        >
          {/* DEV label + timer */}
          <Box
            onClick={() => setPanelOpen(true)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.5, py: 0.75,
              cursor: 'pointer',
              '&:hover': { bgcolor: alpha(accentColor, 0.1) },
              transition: 'background 0.15s',
            }}
          >
            <BugReportIcon sx={{ fontSize: 18, color: accentColor }} />
            <Typography sx={{
              fontSize: 12, fontWeight: 800, fontFamily: 'monospace',
              color: accentColor, letterSpacing: 1,
            }}>
              DEV
            </Typography>
            <Typography sx={{
              fontSize: 11, fontWeight: 600, fontFamily: 'monospace',
              color: 'text.secondary', minWidth: 36,
            }}>
              {elapsed}
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ width: 1, height: 24, bgcolor: alpha(accentColor, 0.2) }} />

          {/* Event counter */}
          <Tooltip title={`${state.count} events captured${hasErrors ? `, ${state.errors} errors` : ''}`}>
            <Box
              onClick={() => setPanelOpen(true)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.25, py: 0.75,
                cursor: 'pointer',
                '&:hover': { bgcolor: alpha(accentColor, 0.1) },
                transition: 'background 0.15s',
              }}
            >
              <Box sx={{
                bgcolor: accentColor, color: '#fff',
                fontSize: 10, fontWeight: 800, fontFamily: 'monospace',
                minWidth: 28, height: 20, borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5,
              }}>
                {state.count}
              </Box>
              {!isMobile && (
                <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 500 }}>
                  events
                </Typography>
              )}
              {hasErrors && (
                <Box sx={{
                  bgcolor: '#ef4444', color: '#fff',
                  fontSize: 10, fontWeight: 800, fontFamily: 'monospace',
                  minWidth: 24, height: 20, borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                }}>
                  {state.errors}!
                </Box>
              )}
            </Box>
          </Tooltip>

          {/* Divider */}
          <Box sx={{ width: 1, height: 24, bgcolor: alpha(accentColor, 0.2) }} />

          {/* c4 v4 #5 — manual state snapshot button */}
          <Tooltip title="Capture state snapshot now (canvas state + React Flow internal + DOM)">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                debugService.captureManualSnapshot('user_clicked_snapshot_button');
                toast.showToast('State snapshot captured — will appear in next export', 'info');
              }}
              sx={{
                mr: 0.5,
                color: accentColor,
                bgcolor: alpha(accentColor, 0.08),
                '&:hover': { bgcolor: alpha(accentColor, 0.2) },
              }}
            >
              <CameraAltIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Start / active Debug Session (free-roam capture builder) */}
          <Tooltip title={debugSession.active ? 'Debug session in progress (see the panel, lower-right)' : 'Start a debug session — walk the app, capture screenshots + notes, export a ZIP'}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); if (!debugSession.active) void handleStartSession(); }}
              sx={{
                mr: 0.5,
                color: debugSession.active ? '#ef4444' : accentColor,
                bgcolor: alpha(debugSession.active ? '#ef4444' : accentColor, 0.08),
                '&:hover': { bgcolor: alpha(debugSession.active ? '#ef4444' : accentColor, 0.2) },
                ...(debugSession.active ? { animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } } : {}),
              }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Download button */}
          <Tooltip title="Download session log (JSON + HTML)">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); debugService.downloadExport(); debugService.downloadHtmlReport(); }}
              sx={{
                mx: 0.5,
                color: accentColor,
                bgcolor: alpha(accentColor, 0.08),
                '&:hover': { bgcolor: alpha(accentColor, 0.2) },
              }}
            >
              <FileDownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Open Panel button */}
          <Tooltip title="Open Dev Console">
            <IconButton
              size="small"
              onClick={() => setPanelOpen(true)}
              sx={{
                mr: 0.5,
                color: 'text.secondary',
                '&:hover': { bgcolor: alpha(accentColor, 0.1) },
              }}
            >
              <TerminalIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <DebugPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
      <DebugSessionOverlay />
    </>
  );
};

export default DevModeIndicator;

export const DEV_BANNER_HEIGHT = 40;
